<template>
  <el-card class="feishu-authorization" v-loading="loading">
    <div slot="header" class="feishu-authorization-header">
      <span>飞书授权</span>
      <el-tag :type="authorization.status === 'authorized' ? 'success' : 'info'" size="small">{{ authorization.status_label || '未授权' }}</el-tag>
    </div>
    <p class="feishu-authorization-description">授权本人的飞书账号，供客诉反馈等业务使用。退出后台后，授权仍可用于后续业务。</p>
    <el-alert v-if="error" :title="error" type="error" :closable="false" show-icon class="feishu-authorization-alert" />
    <el-alert v-if="loaded && !authorization.configured" title="飞书授权尚未配置完成，请联系管理员。" type="warning" :closable="false" show-icon class="feishu-authorization-alert" />
    <dl v-if="authorization.status !== 'unbound'" class="feishu-authorization-details">
      <dt>飞书姓名</dt><dd>{{ authorization.name || '—' }}</dd>
      <template v-if="authorization.require_phone !== false"><dt>手机号</dt><dd>{{ authorization.mobile || '—' }}</dd></template>
      <dt>授权时间</dt><dd>{{ authorization.authorized_at || '—' }}</dd>
    </dl>
    <p v-if="authorization.status === 'insufficient'" class="feishu-authorization-description">授权权限不足，请重新授权以补充业务所需权限。</p>
    <p v-if="authorization.status === 'reauthorize'" class="feishu-authorization-description">当前授权需要重新确认，请重新授权后继续使用。</p>
    <div class="feishu-authorization-actions">
      <el-button type="primary" :loading="busy" :disabled="loading || !loaded || !authorization.configured" @click="authorize">{{ authorization.status === 'unbound' ? '授权飞书账号' : '重新授权' }}</el-button>
      <el-button v-if="authorization.status !== 'unbound'" :disabled="busy || loading" @click="unlink">解除绑定</el-button>
      <el-button :disabled="busy || loading" @click="refresh">刷新状态</el-button>
    </div>
    <p class="feishu-authorization-note">{{ identityNotice }}解除绑定后，本系统将停止使用该授权。</p>
  </el-card>
</template>

<script>
import { getFeishuAuthorization, beginFeishuAuthorization, completeFeishuAuthorization, unlinkFeishuAuthorization } from '@/api/feishuAuthorization';

export default {
  data() {
    return { authorization: { status: 'unbound', configured: false }, loading: false, busy: false, loaded: false, error: '' };
  },
  computed: {
    identityNotice() {
      return this.authorization.require_phone === false
        ? '请使用当前后台账号已绑定的本企业飞书账号授权；未绑定飞书身份的账号暂不支持授权。'
        : '飞书手机号须与当前后台账号手机号一致。';
    },
  },
  created() {
    this.initialize();
  },
  methods: {
    async initialize() {
      const state = this.$route.query.feishu_state;
      if (state !== undefined) {
        const query = { ...this.$route.query };
        delete query.feishu_state;
        // 完成前清理地址，避免授权凭据跟随浏览器地址留存或被分享。
        this.busy = true;
        try {
          await this.$router.replace({ path: this.$route.path, query });
          if (typeof state !== 'string' || !/^[a-f0-9]{64}$/.test(state)) throw new Error('授权请求已失效，请重新授权');
          this.authorization = await completeFeishuAuthorization(state);
          this.loaded = true;
          if (this.authorization.status === 'authorized') this.$message.success('飞书授权成功');
        } catch (error) {
          this.error = error.message || '授权未完成，请重新发起';
        } finally {
          this.busy = false;
        }
      }
      await this.refresh(false);
    },
    async refresh(clearError = true) {
      if (this.loading || this.busy) return;
      this.loading = true;
      if (clearError) this.error = '';
      try {
        this.authorization = await getFeishuAuthorization();
        this.loaded = true;
      } catch (error) {
        this.error = error.message || '无法获取授权状态，请稍后重试';
      } finally {
        this.loading = false;
      }
    },
    async authorize() {
      if (this.busy || this.loading || !this.loaded || !this.authorization.configured) return;
      this.busy = true;
      this.error = '';
      try {
        const { url } = await beginFeishuAuthorization();
        const target = new URL(url);
        if (target.origin !== 'https://accounts.feishu.cn' || target.pathname !== '/open-apis/authen/v1/authorize' || target.username || target.password) {
          throw new Error('授权地址无效，请联系管理员');
        }
        window.location.assign(target.href);
      } catch (error) {
        this.error = error.message || '无法发起飞书授权';
        this.busy = false;
      }
    },
    async unlink() {
      if (this.busy || this.loading) return;
      this.busy = true;
      try {
        await this.$confirm('解除后，本系统将停止使用该飞书授权，后续业务可能改由默认机器人发送。是否继续？', '解除飞书绑定', { type: 'warning' });
        this.error = '';
        this.authorization = await unlinkFeishuAuthorization();
        this.$message.success('已解除绑定');
      } catch (error) {
        if (error !== 'cancel' && error !== 'close') this.error = error.message || '解除绑定失败，请重试';
      } finally {
        this.busy = false;
      }
    },
  },
};
</script>

<style scoped>
.feishu-authorization { margin-bottom: 20px; }
.feishu-authorization-header { display: flex; justify-content: space-between; align-items: center; }
.feishu-authorization-description { color: #606266; font-size: 14px; line-height: 1.7; margin-bottom: 16px; }
.feishu-authorization-alert { margin-bottom: 16px; }
.feishu-authorization-details { display: grid; grid-template-columns: 86px 1fr; gap: 14px; margin: 20px 0; font-size: 14px; }
.feishu-authorization-details dt { color: #909399; }
.feishu-authorization-details dd { margin: 0; color: #303133; overflow-wrap: break-word; }
.feishu-authorization-actions { display: flex; flex-wrap: wrap; gap: 10px; }
.feishu-authorization-actions .el-button { margin-left: 0; }
.feishu-authorization-note { color: #909399; font-size: 12px; line-height: 1.7; margin-top: 16px; }
</style>
